import React, {
  useRef,
} from 'react';
import AmountCell from 'pages/Dashboard/pages/Billing/Payments/AmountCell';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  formatDate,
} from 'utils/date';
import {
  RefundRow,
} from 'views/EMR/Billing/utils';
import ToolTip from 'core/components/Tooltip';
import Name from 'pages/Dashboard/components/Name';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

type SourceCellProps = {
  row: RefundRow;
};

function SourceCell({ row }: SourceCellProps) {
  const ref = useRef<HTMLElement>(null);
  return (
    <div className="gap-px flex flex-col font-semibold w-full min-w-0">
      <span className="text-gray-500">
        {row?.isCredit ? (
          <span className="text-gray-500">
            Credit
          </span>
        ) : (
          <div className="gap-px flex flex-col font-semibold w-full min-w-0">
            <span className="text-gray-500">
              {formatDate(row?.startDateTime ?? '')}
            </span>
            <ToolTip
              title={row?.typeOfVisitDescription ?? ''}
              auto
              ref={ref}
            >
              <small ref={ref} className="truncate block">
                {row?.typeOfVisitDescription ?? '--'}
              </small>
            </ToolTip>
          </div>
        )}
      </span>
    </div>
  );
}

type RefundedByCellProps = {
  row: RefundRow;
};

function RefundedByCell({ row }: RefundedByCellProps) {
  const src = useUserProfilePicturePath(row.refundedByUserId);

  return row.refundedByUserName === '--' ? (
    <p className="text-gray-500 font-semibold">
      --
    </p>
  ) : (
    <ToolTip
      title={row.refundedByUserName ?? ''}
      placement="top"
      hide={row.refundedByUserName === '--'}
    >
      <span className="truncate">
        <Name
          name={row.refundedByUserName ?? ''}
          image={{ src }}
        />
      </span>
    </ToolTip>
  );
}

const refundLogsColumns: ColumnDef<RefundRow>[] = [
  {
    id: 'createdOn',
    accessorKey: 'createdOn',
    columnTitle: 'Refunded on',
    size: 150,
    cell: ({ row: { original: { createdOn } } }) => (
      <span>
        {formatDate(createdOn ?? '')}
      </span>
    ),
  },
  {
    id: 'typeOfVisit',
    accessorKey: 'typeOfVisitDescription',
    columnTitle: 'Source',
    size: 180,
    cell: ({ row }) => <SourceCell row={row.original} />,
  },
  {
    id: 'refundedAmount',
    accessorKey: 'refundedAmount',
    columnTitle: 'Amount',
    size: 150,
    cell: ({ row, column }) => (
      <AmountCell<RefundRow>
        value={row.original?.refundedAmount ?? 0}
        columnDef={column.columnDef}
        showZero
        showNegative={false}
      />
    ),
  }, {
    id: 'movedBy',
    accessorKey: 'movedByUser',
    columnTitle: 'Refunded By',
    size: 200,
    cell: ({ row }) => <RefundedByCell row={row.original} />,
  },
];

export default refundLogsColumns;
